/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/attachmentSelect.scss';

import React, {MouseEventHandler, useRef} from 'react';
import {AwesomeIcon, ChangeFunction, Checkbox, Loader, WithBackground} from '@dvrd/dvr-controls';
import Attachment from "../../../models/attachmentModel";
import AttachmentUpload from "./upload/attachmentUpload";
import prettyBytes from 'pretty-bytes';
import {BolmateButton} from "../../controls";

interface Props {
    onSelect: (attachment: Attachment) => ChangeFunction<boolean>;
    onClose: MouseEventHandler;
    onSubmit: MouseEventHandler;
    onAddAttachments: (attachments: Attachment[]) => void;
    onSearch: ChangeFunction<string>;
    onStartUpload: VoidFunction;
    onDelete: (attachment: Attachment) => MouseEventHandler;
    onClickWithInvoice: VoidFunction;
    onDeselectInvoice: ChangeFunction<boolean>;
    attachments: Attachment[];
    selectedAttachments: Attachment[];
    searchValue: string;
    loading: boolean;
    active: boolean;
    withInvoice: boolean;
}

export default function AttachmentSelect(props: Props) {
    const {
            attachments, onAddAttachments, selectedAttachments, onSelect, searchValue, onSearch, onClose, onSubmit,
            loading, active, onStartUpload, onDelete, onClickWithInvoice, withInvoice, onDeselectInvoice
        } = props,
        searchRef = useRef<HTMLInputElement>(null);

    function onClickSearchIcon() {
        searchRef.current?.focus();
    }

    function _onSearch(evt: React.ChangeEvent<HTMLInputElement>) {
        onSearch(evt.target.value);
    }

    function _onSelectAttachment(attachment: Attachment) {
        return function () {
            onSelect(attachment)(true);
        };
    }

    function _onDeselectAttachment(attachment: Attachment) {
        return function () {
            onSelect(attachment)(false);
        }
    }

    function renderAttachment(attachment: Attachment, index: number) {
        return (
            <div className='row available' key={index} onClick={_onSelectAttachment(attachment)}>
                <Checkbox checked={false} onCheck={onSelect(attachment)}/>
                <label>{attachment.name}</label>
                <label>{prettyBytes(attachment.size)}</label>
                <AwesomeIcon name='trash-alt' className='trash-icon' onClick={onDelete(attachment)}/>
            </div>
        );
    }

    function renderSelectedAttachment(attachment: Attachment, index: number) {
        return (
            <div className='row selected' key={index} onClick={_onDeselectAttachment(attachment)}>
                <Checkbox checked onCheck={onSelect(attachment)}/>
                <label>{attachment.name}</label>
            </div>
        )
    }

    function renderIncludeInvoice() {
        if (withInvoice) return null;
        return (
            <BolmateButton onClick={onClickWithInvoice} label='+ Factuur van order toevoegen als bijlage'
                           className='include-invoice' secondary/>
        )
    }

    function renderExcludeInvoice() {
        if (!withInvoice) return null;
        return (
            <div className='row selected invoice'>
                <Checkbox checked onCheck={onDeselectInvoice}/>
                <label>Factuur van order (.pdf)</label>
            </div>
        )
    }

    const selectedCount = selectedAttachments.length + (withInvoice ? 1 : 0),
        selectedLabel = selectedCount === 1 ? 'BIJLAGE' : 'BIJLAGES';
    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='attachment-selector default-popup'>
                <div className='header-container'>
                    <label className='header-title'>Selecteer bijlage(s) voor deze mail</label>
                </div>
                <div className='body-container'>
                    <Loader active={loading}/>
                    <div className='toolbar'>
                        <div className='search-field'>
                            <AwesomeIcon name='search' className='icon' onClick={onClickSearchIcon}/>
                            <input className='input' value={searchValue} onChange={_onSearch} placeholder='Zoeken'
                                   ref={searchRef}/>
                        </div>
                        <AttachmentUpload onRefresh={onAddAttachments} onUpload={onStartUpload}/>
                    </div>
                    <div className='row available head'>
                        <div/>
                        <label>NAAM</label>
                        <label>GROOTTE</label>
                        <div/>
                    </div>
                    <div className='attachments'>
                        <div className='rows available'>
                            {attachments.map(renderAttachment)}
                        </div>
                        {renderIncludeInvoice()}
                        <div className='rows selected'>
                            <div className='row selected head'>
                                <div/>
                                <label>{selectedCount} {selectedLabel} GESELECTEERD</label>
                            </div>
                            <div className='selected-attachments'>
                                {selectedAttachments.map(renderSelectedAttachment)}
                                {renderExcludeInvoice()}
                            </div>
                        </div>
                    </div>
                </div>
                <div className='footer-container'>
                    <BolmateButton onClick={onSubmit} label='Klaar' disabled={loading} secondary/>
                </div>
            </div>
        </WithBackground>
    )
}